import { useMemo } from 'react';
import type { UseSheetData } from '@/hooks/useSheetData';
import { Card, CardHeader } from '@/components/ui/Card';
import { REFRESH_MS } from '@/config/app';
import {
  CONTRACT_DEMAND_KVA,
  DEMAND_CHARGE_NORMAL_RS_KVA, DEMAND_CHARGE_PENAL_RS_KVA,
  ELECTRICITY_DUTY_RS_KVAH, CUSTOMER_CHARGES_RS_MONTH,
  TARIFF_RATES_T1, TOD_WINDOWS,
} from '@/config';
// import { TARIFF_RATES_T2, TARIFF_RATES_T3 } from '@/config';
import { useAuth } from '@/auth/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { formatRelative } from '@/lib/formatters';
import { Database, Gauge, Info, RefreshCw } from 'lucide-react';
import {
  AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import type { TelemetryRow } from '@/types';

interface SettingsProps { data: UseSheetData }

/* -------------------------------------------------------------------------- */
/*  PiStatsCard — Edge Node health panel                                      */
/* -------------------------------------------------------------------------- */

function PiStatsCard({ rows }: { rows: TelemetryRow[] }) {
  const latest = rows.length ? rows[rows.length - 1] : null;
  const hasData = !!(latest && (latest.piTempC > 0 || latest.piMemPct > 0));

  // Last 24h temperature series
  const tempSeries = useMemo(() => {
    const cutoff = Date.now() - 24 * 3_600_000;
    return rows
      .filter(r => r.timestamp.getTime() >= cutoff)
      .filter(r => r.piTempC > 0 || r.piMemPct > 0)
      .map(r => ({
        label: r.timestamp.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
        temp: +r.piTempC.toFixed(1),
        ram:  +r.piMemPct.toFixed(1),
      }));
  }, [rows]);

  const temp    = latest?.piTempC  ?? 0;
  const ram     = latest?.piMemPct ?? 0;
  const cpu     = latest?.piCpuPct ?? 0;
  const tempColor = temp > 75 ? '#EF4444' : temp > 65 ? '#F59E0B' : hasData ? '#10B981' : '#94A3B8';
  const ramColor  = ram  > 85 ? '#EF4444' : ram  > 70 ? '#F59E0B' : hasData ? '#10B981' : '#94A3B8';
  const cpuColor  = cpu  > 85 ? '#EF4444' : cpu  > 70 ? '#F59E0B' : hasData ? '#10B981' : '#94A3B8';

  const piHealthYTicks = [0, 20, 40, 60, 80, 100];

  // Get key time ticks
  const keyHours = ['06:00', '12:00', '18:00', '00:00'];
  const tickSet  = new Set<string>();
  if (tempSeries.length) tickSet.add(tempSeries[tempSeries.length - 1].label);
  for (const h of keyHours) { if (tempSeries.find(d => d.label === h)) tickSet.add(h); }
  const ticks = Array.from(tickSet);

  return (
    <Card>
      <CardHeader
        eyebrow="Edge Node · Raspberry Pi Health"
        title={latest?.deviceId || latest?.meterSerial || 'Raspberry Pi Gateway'}
      />
      {!hasData && (
        <p className="text-[11px] text-amber-600 font-medium mb-3">
          ⚠ No Pi telemetry in current data — fields piTempC / piMemPct not populated
        </p>
      )}

      {/* Live stats strip */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {([
          { label: 'Temp', val: `${temp.toFixed(0)}°C`, color: tempColor, icon: '🌡' },
          { label: 'RAM',  val: `${ram.toFixed(0)}%`,   color: ramColor,  icon: '🧠' },
          { label: 'CPU',  val: `${cpu.toFixed(0)}%`,   color: cpuColor,  icon: '⚡' },
        ]).map(({ label, val, color, icon }) => (
          <div key={label} className="rounded-xl bg-ink-50 px-2 py-2.5 text-center">
            <div className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-1">
              {icon} {label}
            </div>
            <p className="num text-base font-bold" style={{ color }}>{val}</p>
          </div>
        ))}
      </div>

      {/* Temperature area chart */}
      {tempSeries.length > 1 && (
        <>
          <p className="text-[10px] font-bold uppercase tracking-wide text-ink-500 mb-2">
            Pi Temperature · Last 24 h
          </p>
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer>
              <AreaChart data={tempSeries} margin={{ top: 8, right: 4, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="piTempGradSettings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={tempColor} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={tempColor} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="label" tickLine={false} axisLine={false}
                  ticks={ticks}
                  tick={{ fontSize: 10, fontWeight: 700, fill: '#475569' }}
                />
                <YAxis
                  tickLine={false} axisLine={false} width={36}
                  domain={[0, 100]}
                  ticks={piHealthYTicks}
                  interval={0}
                  allowDecimals={false}
                  tick={{ fontSize: 10, fontWeight: 700, fill: '#475569' }}
                  tickFormatter={v => `${v}`}
                />
                <Tooltip
                  cursor={{ stroke: '#94A3B8', strokeWidth: 1, strokeDasharray: '3 3' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs">
                        <div className="font-bold text-slate-900 mb-1">{label}</div>
                        <div className="flex gap-3">
                          <span style={{ color: tempColor }} className="font-bold">
                            🌡 {payload[0]?.value}°C
                          </span>
                          {payload[1] && (
                            <span style={{ color: '#6366F1' }} className="font-bold">
                              🧠 {payload[1].value}%
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                <ReferenceLine y={75} stroke="#EF4444" strokeDasharray="4 2"
                  label={{ value: '75°C', position: 'insideTopRight', fontSize: 9, fontWeight: 700, fill: '#EF4444' }} />
                <ReferenceLine y={65} stroke="#F59E0B" strokeDasharray="4 2"
                  label={{ value: '65°C', position: 'insideTopRight', fontSize: 9, fontWeight: 700, fill: '#F59E0B' }} />
                <Area
                  type="monotone" dataKey="temp" name="Temp"
                  stroke={tempColor} strokeWidth={2}
                  fill="url(#piTempGradSettings)"
                  dot={false} activeDot={{ r: 3.5, strokeWidth: 0 }}
                />
                <Area
                  type="monotone" dataKey="ram" name="RAM"
                  stroke="#6366F1" strokeWidth={1.5}
                  fill="none"
                  dot={false} activeDot={{ r: 3, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-2 text-[10px] font-semibold text-ink-500">
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 rounded" style={{ background: tempColor }} />
              Temperature (°C)
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 rounded bg-indigo-500" />
              RAM Usage (%)
            </span>
          </div>
        </>
      )}
      {latest?.piThermalThrottle && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs font-semibold text-red-700">
          ⚠️ Thermal throttle active — Pi is running hot, performance reduced
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Settings page                                                        */
/* -------------------------------------------------------------------------- */

export function Settings({ data }: SettingsProps) {
  const { selectedSheet, user } = useAuth();
  const { tariff, stateCode, contractDemandKva: tenantCd } = useTenant();
  const refreshSec = Math.round(REFRESH_MS / 1000);

  // Contract Demand (CD): per-sheet override → tariff default → TGSPDCL fallback
  const contractDemandKva = selectedSheet?.meta?.contractDemandKva ?? tenantCd ?? CONTRACT_DEMAND_KVA;

  // FIX 1: site name — use label as-is, remove the hardcoded RALPRO→RALPRO_Technocrafts replacement
  const siteName = selectedSheet?.label ?? user?.access.sheets[0]?.label ?? 'Vireon Pilot';

  const isWB = stateCode === 'west-bengal';

  return (
    <div className="space-y-3 pt-3 pb-2 animate-fade-in">
      <h1 className="px-1 font-display text-xl font-semibold text-ink-900">Settings</h1>

      {/* ── Site & Data Stats ── */}
      <Card>
        {/* FIX 1: title now uses siteName directly, no .replace() override */}
        <CardHeader
          eyebrow="Site"
          title={siteName}
          description={selectedSheet?.meta?.location ?? 'Telangana'}
        />
        <Row icon={<Gauge className="w-4 h-4" />}     label="Meters discovered"  value={selectedSheet ? '1' : '0'} />
        <Row icon={<Database className="w-4 h-4" />}  label="Samples loaded"     value={String(data.rows.length)} />
        <Row icon={<RefreshCw className="w-4 h-4" />} label="Last refresh"        value={data.fetchedAt ? formatRelative(data.fetchedAt) : 'never'} />
        <Row icon={<RefreshCw className="w-4 h-4" />} label="Refresh interval"    value={`${refreshSec} s`} />
      </Card>

      {/* ── Edge Node Health ── */}
      <PiStatsCard rows={data.rows} />

      {/* ── Tariff Reference — state-aware ── */}
      {isWB ? (
        // ── FIX 5: West Bengal (WBSEDCL B-IDIT) tariff block ──────────────────
        <Card>
          <CardHeader
            eyebrow={`WBSEDCL · ${selectedSheet?.meta?.tariffCode ?? 'B-IDIT'}`}
            title="Active tariff"
            description={`Consumer ${selectedSheet?.meta?.consumerId ?? '—'} · Contract Demand (CD) ${contractDemandKva} kVA · 0.4 kV supply`}
          />

          {/* ToD energy rates */}
          <div className="space-y-1">
            <KV k={`Off-Peak  (${tariff.todWindows['OFF-PEAK'].range})`} v={`₹${tariff.energyRates['OFF-PEAK'].toFixed(2)}/kWh`} />
            <KV k={`Normal    (${tariff.todWindows['NORMAL'].range})`}   v={`₹${tariff.energyRates['NORMAL'].toFixed(2)}/kWh`}   />
            <KV k={`Peak      (${tariff.todWindows['PEAK'].range})`}     v={`₹${tariff.energyRates['PEAK'].toFixed(2)}/kWh`}     />
          </div>

          {/* FIX 4: full name first, abbreviation in parentheses */}
          <div className="mt-2 mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-400 px-1">
            Demand charges — when Maximum Demand (MD) exceeds Contract Demand (CD)
          </div>
          <div className="space-y-1">
            <KV k="Minimum chargeable demand"
                v={`${(contractDemandKva * tariff.minDemandPct).toFixed(0)} kVA  (${(tariff.minDemandPct * 100).toFixed(0)}% of CD)`} />
            <KV k="Demand charge (up to CD)"
                v={`₹${tariff.demandChargeRsKva}/kVA/month`} />
            <KV k="Excess demand penalty (MD above CD)"
                v={`₹${tariff.demandPenalRsKva}/kVA/month`} />
            <KV k="Off-peak max demand ceiling"
                v={`${(tariff.offPeakMaxDemandPct * 100).toFixed(0)}% of CD`} />
          </div>

          <div className="border-t border-ink-100 my-2" />

          {/* Fixed & statutory charges */}
          <div className="space-y-1">
            <KV k="Contract Demand (CD)"            v={`${contractDemandKva} kVA`} />
            <KV k="Electricity duty"                v={`${(tariff.electricityDutyValue * 100).toFixed(1)}% of net bill`} />
            <KV k="Meter rental"                    v={`₹${tariff.meterRentalRsMonth.toLocaleString('en-IN')}/month`} />
            <KV k="Timely Payment Rebate (TPR)"     v={`${(tariff.tprFraction * 100).toFixed(2)}% of gross bill`} />
            <KV k="Late payment surcharge"          v="None (WBSEDCL B-IDIT)" />
          </div>

          <div className="border-t border-ink-100 my-2" />

          {/* Power Factor adjustment rules — read directly from PF_ADJUSTMENT_TABLE */}
          <div className="mt-2 mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-400 px-1">
            Power Factor (PF) adjustment — applied as % of energy charge per ToD period
          </div>
          <div className="space-y-1">
            <KV k="PF ≥ 0.99"           v="+8% Normal · +9% Peak · +7% Off-Peak (rebate)" />
            <KV k="PF 0.98 – 0.99"      v="+7% Normal · +8% Peak · +6% Off-Peak (rebate)" />
            <KV k="PF 0.97 – 0.98"      v="+5% Normal · +6% Peak · +4% Off-Peak (rebate)" />
            <KV k="PF 0.96 – 0.97"      v="+4% Normal · +5% Peak · +3% Off-Peak (rebate)" />
            <KV k="PF 0.95 – 0.96"      v="+3% Normal · +4% Peak · +2% Off-Peak (rebate)" />
            <KV k="PF 0.94 – 0.95"      v="+2.25% Normal · +3% Peak · +1.5% Off-Peak (rebate)" />
            <KV k="PF 0.93 – 0.94"      v="+1.5% Normal · +2% Peak · +1% Off-Peak (rebate)" />
            <KV k="PF 0.92 – 0.93"      v="+0.75% Normal · +1% Peak · +0.5% Off-Peak (rebate)" />
            <KV k="PF 0.86 – 0.92"      v="No adjustment" />
            <KV k="PF 0.85 – 0.86"      v="−1% Normal · −1.25% Peak · −0.75% Off-Peak (penalty)" />
            <KV k="PF 0.84 – 0.85"      v="−2% Normal · −2.5% Peak · −1.5% Off-Peak (penalty)" />
            <KV k="PF 0.83 – 0.84"      v="−2.5% Normal · −3.25% Peak · −1.75% Off-Peak (penalty)" />
            <KV k="PF 0.82 – 0.83"      v="−3% Normal · −4% Peak · −2% Off-Peak (penalty)" />
            <KV k="PF 0.81 – 0.82"      v="−4% Normal · −5% Peak · −3% Off-Peak (penalty)" />
            <KV k="PF 0.80 – 0.81"      v="−5% Normal · −6% Peak · −4% Off-Peak (penalty)" />
            <KV k="PF < 0.80"           v="−6% Normal · −7% Peak · −5% Off-Peak (penalty)" />
          </div>

          <div className="border-t border-ink-100 my-2" />

          {/* Load Factor rebate / surcharge rules — read directly from LF_REBATE_TABLE */}
          <div className="mt-2 mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-400 px-1">
            Load Factor (LF) rebate / surcharge — applied per kWh consumed
          </div>
          <div className="space-y-1">
            <KV k="LF < 25%"            v="35 paise/kWh surcharge" />
            <KV k="LF 25% – 30%"        v="15 paise/kWh surcharge" />
            <KV k="LF 30% – 60%"        v="14 paise/kWh surcharge" />
            <KV k="LF 60% – 65%"        v="7 paise/kWh surcharge" />
            <KV k="LF 65% – 70%"        v="No adjustment" />
            <KV k="LF 70% – 75%"        v="6 paise/kWh rebate" />
            <KV k="LF 75% – 80%"        v="11 paise/kWh rebate" />
            <KV k="LF 80% – 85%"        v="16 paise/kWh rebate" />
            <KV k="LF 85% – 90%"        v="21 paise/kWh rebate" />
            <KV k="LF 90% – 92%"        v="26 paise/kWh rebate" />
            <KV k="LF 92% – 95%"        v="31 paise/kWh rebate" />
            <KV k="LF ≥ 95%"            v="36 paise/kWh rebate" />
          </div>
        </Card>
      ) : (
        // ── TGSPDCL HT (Telangana) tariff block — unchanged ────────────────────
        <Card>
          <CardHeader
            eyebrow={`TGSPDCL · ${selectedSheet?.meta?.tariffCode ?? 'HT Cat.1A'}`}
            title="Active tariff"
            description={`Consumer ${selectedSheet?.meta?.consumerId ?? '—'} · Contract Demand (CD) ${contractDemandKva} kVA · 33 kV supply`}
          />

          {/* ToD period windows */}
          <div className="space-y-1">
            <KV k={`Off-Peak  (${TOD_WINDOWS['OFF-PEAK'].range})`} v={`₹${TARIFF_RATES_T1['OFF-PEAK'].toFixed(2)}/kVAh`} />
            <KV k={`Normal    (${TOD_WINDOWS['NORMAL'].range})`}   v={`₹${TARIFF_RATES_T1['NORMAL'].toFixed(2)}/kVAh`}   />
            <KV k={`Peak      (${TOD_WINDOWS['PEAK'].range})`}     v={`₹${TARIFF_RATES_T1['PEAK'].toFixed(2)}/kVAh`}     />
          </div>

          <div className="border-t border-ink-100 my-2" />

          {/* Fixed charges */}
          <div className="space-y-1">
            <KV k="Contract Demand (CD)"            v={`${contractDemandKva} kVA`} />
            <KV k="Min. chargeable demand"          v={`${(contractDemandKva * 0.80).toFixed(0)} kVA  (80% of CD)`} />
            <KV k="Demand charge — Normal"          v={`₹${DEMAND_CHARGE_NORMAL_RS_KVA}/kVA/month`} />
            <KV k="Demand charge — Penal"           v={`₹${DEMAND_CHARGE_PENAL_RS_KVA}/kVA/month  (excess above CD)`} />
            <KV k="Electricity duty"                v={`${(ELECTRICITY_DUTY_RS_KVAH * 100).toFixed(0)} paise/kVAh  (6p flat)`} />
            <KV k="Customer charges"                v={`₹${CUSTOMER_CHARGES_RS_MONTH.toLocaleString('en-IN')}/month`} />
            <KV k="Late payment surcharge"          v="1.25% of bill  (if paid after 15 days)" />
          </div>
        </Card>
      )}

      {/* ── About ── */}
      {/* FIX 3: removed "Mobile-first energy intelligence... Add to home screen" sentence */}
      <Card>
        <CardHeader eyebrow="About" title="CORTEX by Vireon" description="Production build · v1.0" />
        <p className="text-xs text-ink-500 leading-relaxed flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Auto-refreshes every {refreshSec}s when the tab is visible.
        </p>
      </Card>
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0 border-ink-100">
      <div className="flex items-center gap-2 text-ink-700 text-xs font-semibold">
        <span className="text-ink-400">{icon}</span>{label}
      </div>
      <div className="num text-xs font-semibold text-ink-900">{value}</div>
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <span className="text-ink-500 shrink-0">{k}</span>
      <span className={`text-ink-800 font-semibold text-right break-all max-w-[60%] ${mono ? 'font-mono text-[10px]' : ''}`}>{v}</span>
    </div>
  );
}